import { Guid } from "../../../types/guid.type";
import { EntityDocumentStatusBase, IEntityDocumentStatusBaseInfo } from "../../entity/document/entity-document-status.base";
import { IPersonInfo, PersonInfo } from "../../core/person-info";
import { EquipmentModel, IEquipmentModelInfo } from "./equipment-model";
import { EquipmentType, IEquipmentTypeInfo } from "./equipment-type";
import type { EquipmentAssignment, IEquipmentAssignmentInfo } from "./equipment-assignment";
import { instantiateEquipmentAssignment } from "./equipment-lazy-instantiation";
import { LvfEquipmentAcquisitionMode } from "../../../enum/supplychain/lvf-equipment-acquisition-mode";
import { MultiStatusConfig } from "../../multi-status/multi-status-config";
import { IMultiStatusConfigInfo } from "../../multi-status/multi-status-config";
import { MultiStatus } from "../../multi-status/multi-status";
import { IMultiStatusInfo } from "../../multi-status/multi-status";

export interface IEquipmentInfo extends IEntityDocumentStatusBaseInfo {

  AgencyUid?: Guid;
  NumeroParc?: string;
  RegistrationNumber?: string;
  Icon?: string;
  Designation?: string;
  IdentificationInfo?: string;
  FirstUsageDate?: Date;
  FirstUsageDateString?: string;
  ParkingEntryDate?: Date;
  ParkingEntryDateString?: string;
  RentalEndDate?: Date;
  RentalEndDateString?: string;
  SerialNumber?: string;
  EquipmentTypeUid: Guid;
  EquipmentType?: IEquipmentTypeInfo;
  EquipmentModelUid?: Guid;
  Model?: IEquipmentModelInfo;
  LinkedEquipmentUid?: Guid;
  InitialMileageValue?: number;
  DirectBookingAllowed?: boolean;
  AuthorUid?: Guid;
  Author?: IPersonInfo;
  IsRenting?: boolean;
  LvfEquipmentAcquisitionMode?: LvfEquipmentAcquisitionMode;
  AcquisitionModeConfig?: MultiStatusConfig;
  HasBeenReturned?: boolean;
  CurrentAssignment?: IEquipmentAssignmentInfo;
  KilometersUntilMaintenance?: number;
  ContractNegotiatedKilometerValue?: number;
}

export class Equipment extends EntityDocumentStatusBase {

  AgencyUid?: Guid;
  NumeroParc?: string;
  RegistrationNumber?: string;
  Icon?: string;
  Designation?: string;
  IdentificationInfo?: string;
  FirstUsageDate?: Date;
  FirstUsageDateString?: string = '';
  ParkingEntryDate?: Date;
  ParkingEntryDateString?: string = '';
  RentalEndDate?: Date;
  RentalEndDateString?: string = '';
  SerialNumber?: string;
  EquipmentTypeUid!: Guid;
  EquipmentType?: EquipmentType;
  EquipmentModelUid?: Guid;
  Model?: EquipmentModel;
  LinkedEquipmentUid?: Guid;
  InitialMileageValue?: number;
  DirectBookingAllowed?: boolean;
  AuthorUid?: Guid;
  Author?: PersonInfo;
  IsRenting?: boolean;
  LvfEquipmentAcquisitionMode?: LvfEquipmentAcquisitionMode;
  AcquisitionModeConfig?: MultiStatusConfig;
  HasBeenReturned?: boolean;
  CurrentAssignment?: EquipmentAssignment;
  KilometersUntilMaintenance?: number;
  ContractNegotiatedKilometerValue?: number;

  constructor(info: IEquipmentInfo) {
    super(info);

    this.AgencyUid = info.AgencyUid;
    this.NumeroParc = info.NumeroParc;
    this.RegistrationNumber = info.RegistrationNumber;
    this.Designation = info.Designation;
    this.IdentificationInfo = info.IdentificationInfo;
    this.Icon = info.Icon;
    this.FirstUsageDateString = info.FirstUsageDateString;
    this.ParkingEntryDateString = info.ParkingEntryDateString;
    this.SerialNumber = info.SerialNumber;
    this.EquipmentTypeUid = info.EquipmentTypeUid;
    this.EquipmentType = info.EquipmentType ? EquipmentType.createInstance(EquipmentType, info.EquipmentType) : undefined;
    this.EquipmentModelUid = info.EquipmentModelUid;
    this.Model = info.Model ? EquipmentModel.createInstance(EquipmentModel, info.Model) : undefined;
    this.LinkedEquipmentUid = info.LinkedEquipmentUid;
    this.InitialMileageValue = info.InitialMileageValue;
    this.DirectBookingAllowed = info.DirectBookingAllowed ?? false;
    this.AuthorUid = info.AuthorUid;
    this.Author = PersonInfo.createInstance(PersonInfo, info.Author);
    this.LvfEquipmentAcquisitionMode = info.LvfEquipmentAcquisitionMode
      ?? (info.IsRenting ? LvfEquipmentAcquisitionMode.RENTED : LvfEquipmentAcquisitionMode.PURCHASED);
    this.IsRenting = this.LvfEquipmentAcquisitionMode === LvfEquipmentAcquisitionMode.RENTED;
    this.RentalEndDateString = info.RentalEndDateString;
    this.HasBeenReturned = info.HasBeenReturned;
    this.CurrentAssignment = info.CurrentAssignment
      ? instantiateEquipmentAssignment(info.CurrentAssignment)
      : undefined;
    this.KilometersUntilMaintenance = info.KilometersUntilMaintenance;
    this.ContractNegotiatedKilometerValue = info.ContractNegotiatedKilometerValue;

    this.AcquisitionModeConfig = this.buildAcquisitionModeConfig();
    this.changeAcquisitionModeStyle();
  }

  get isRented(): boolean {
    return this.LvfEquipmentAcquisitionMode === LvfEquipmentAcquisitionMode.RENTED;
  }

  override getDatePropertyNames(): string[] {
    return [...super.getDatePropertyNames(), 'FirstUsageDate', 'ParkingEntryDate', 'RentalEndDate'];
  }

  buildAcquisitionModeConfig(): MultiStatusConfig {
    return new MultiStatusConfig(<IMultiStatusConfigInfo>{
      Statuses: [
        new MultiStatus(<IMultiStatusInfo>{
          Label: 'app.equipment.acquisitionMode.purchased',
          Style: 'color: var(--dropdown-bg); background-color: var(--purple-success)',
          Handler: () => {
            this.setAcquisitionMode(LvfEquipmentAcquisitionMode.PURCHASED);
          },
        }),
        new MultiStatus(<IMultiStatusInfo>{
          Label: 'app.equipment.acquisitionMode.rented',
          Style: 'color: var(--module-title); background-color: var(--dropdown-bg)',
          Handler: () => {
            this.setAcquisitionMode(LvfEquipmentAcquisitionMode.RENTED);
          },
        }),
      ],
    });
  }

  changeAcquisitionModeStyle(): void {
    if (!this.AcquisitionModeConfig) {
      return;
    }

    const isPurchased = this.LvfEquipmentAcquisitionMode === LvfEquipmentAcquisitionMode.PURCHASED;
    this.AcquisitionModeConfig.Statuses[0].Style = isPurchased
      ? 'color: var(--dropdown-bg); background-color: var(--purple-success)'
      : 'color: var(--module-title); background-color: var(--dropdown-bg)';
    this.AcquisitionModeConfig.Statuses[1].Style = !isPurchased
      ? 'color: var(--dropdown-bg); background-color: var(--purple-success)'
      : 'color: var(--module-title); background-color: var(--dropdown-bg)';
  }

  setAcquisitionMode(mode: LvfEquipmentAcquisitionMode): void {
    this.LvfEquipmentAcquisitionMode = mode;
    this.IsRenting = mode === LvfEquipmentAcquisitionMode.RENTED;
    this.changeAcquisitionModeStyle();

    if (!this.isRented) {
      this.RentalEndDate = undefined;
      this.RentalEndDateString = '';
      if ('ContractNegotiatedKilometerValue' in this) {
        (this as { ContractNegotiatedKilometerValue?: number }).ContractNegotiatedKilometerValue = undefined;
      }
    }
  }
}
